import { Enum } from 'enumify'
import { ID_LIST_STORE_KEY, LOGIN_TYPE_KEY } from 'Helpers/Constants'
import { encodeParams } from 'Helpers/encodeParams'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'

import { BeFiltersApiContext } from 'Contexts/BeFiltersApiContext'

import AppStateContext from 'Contexts/AppStateContext.js'
import { extractData, withApi } from 'Providers/ApiProvider'
import { LoginTypeEnum } from 'Providers/AuthProvider'

export const withBeFiltersApi = (ComponentToWrap) => {
  const WrappedComponent = (props) => {
    return (
      <BeFiltersApiContext.Consumer>
        {(value) => <ComponentToWrap {...props} beFiltersApi={value} />}
      </BeFiltersApiContext.Consumer>
    )
  }

  WrappedComponent.displayName = `withBeFiltersApi(${
    ComponentToWrap.displayName || ComponentToWrap.name
  })`
  return WrappedComponent
}

const BeFiltersApiProviderComponent = (props) => {
  const { children, apiWrapper } = props

  const { storeGet } = useContext(AppStateContext)

  const endpointsProvider = (baseURL) => {
    return {
      //  execute
      processFilterByConfiguration: (idList, channel, status) => {
        const statusParams = Array.isArray(status)
          ? `&status=${status.map((s) => encodeURIComponent(s)).join(',')}`
          : ''
        return `${baseURL}/api/list/${encodeURIComponent(
          idList,
        )}/process?channel=${encodeURIComponent(channel)}${statusParams}`
      },
      processFilterById: (idList, idFilter, channel, status) => {
        const statusParams = Array.isArray(status)
          ? `&status=${status.map((s) => encodeURIComponent(s)).join(',')}`
          : ''
        return `${baseURL}/api/list/${encodeURIComponent(
          idList,
        )}/process/${encodeURIComponent(idFilter)}?channel=${encodeURIComponent(
          channel,
        )}${statusParams}`
      },
      //  simulate
      simulateFilterByConfiguration: (idList, channel, status) => {
        const statusParams = Array.isArray(status)
          ? `&status=${status.map((s) => encodeURIComponent(s)).join(',')}`
          : ''
        return `${baseURL}/api/list/${encodeURIComponent(
          idList,
        )}/simulate?channel=${encodeURIComponent(channel)}${statusParams}`
      },
      simulateFilterById: (idList, idFilter, channel, status) => {
        const statusParams = Array.isArray(status)
          ? `&status=${status.map((s) => encodeURIComponent(s)).join(',')}`
          : ''
        return `${baseURL}/api/list/${encodeURIComponent(
          idList,
        )}/simulate/${encodeURIComponent(
          idFilter,
        )}?channel=${encodeURIComponent(channel)}${statusParams}`
      },

      //  crud filtri
      getAllFilters: ({ idList }) => {
        const list = idList || storeGet(ID_LIST_STORE_KEY)
        return `${baseURL}/api/list/${encodeURIComponent(list)}/filters`
      },
      createFilter: `${baseURL}/api/filter`,
      getFilter: (idFilter) =>
        `${baseURL}/api/filters/${encodeURIComponent(idFilter)}`,
      deleteFilter: (idFilter) =>
        `${baseURL}/api/filters/${encodeURIComponent(idFilter)}`,
      updateFilter: (idFilter) =>
        `${baseURL}/api/filters/${encodeURIComponent(idFilter)}`,
      //  crud categorie
      getAllCategories: ({
        pageSize,
        pageNumber,
        orderBy,
        orderDescending,
        name,
      }) => {
        orderDescending = orderDescending.toString()
        const params = { pageSize, pageNumber, orderBy, orderDescending, name }
        return `${baseURL}/api/categories?` + encodeParams(params, true)
      },
      createCategory: `${baseURL}/api/category`,
      getCategory: (idCategory) =>
        `${baseURL}/api/categories/${encodeURIComponent(idCategory)}`,
      getCategoryUsage: (idCategory) =>
        `${baseURL}/api/categories/${encodeURIComponent(idCategory)}/checkuse`,
      deleteCategory: (idCategory) =>
        `${baseURL}/api/categories/${encodeURIComponent(idCategory)}`,
      updateCategory: (idCategory) =>
        `${baseURL}/api/categories/${encodeURIComponent(idCategory)}`,
    }
  }

  const endpoints = endpointsProvider(process.env.BEFILTERS_URL)

  const processFilterByConfiguration = (
    idList,
    configuration = [],
    channel = 'Email',
    status = [],
  ) => {
    const url = endpoints.processFilterByConfiguration(idList, channel, status)

    return apiWrapper({
      method: 'post',
      url,
      data: JSON.stringify(configuration),
    }).then(extractData)
  }

  const processFilterById = (
    idList,
    idFilter,
    channel = 'Email',
    status = [],
  ) => {
    const url = endpoints.processFilterById(idList, idFilter, channel, status)

    return apiWrapper({
      method: 'post',
      url,
    }).then(extractData)
  }

  const simulateFilterByConfiguration = (
    idList,
    configuration = [],
    channel = 'Email',
    status = [],
  ) => {
    const url = endpoints.simulateFilterByConfiguration(idList, channel, status)

    return apiWrapper({
      method: 'post',
      url,
      data: JSON.stringify(configuration),
    }).then(extractData)
  }

  const simulateFilterById = (
    idList,
    idFilter,
    channel = 'Email',
    status = [],
  ) => {
    const url = endpoints.simulateFilterById(idList, idFilter, channel, status)

    return apiWrapper({
      method: 'post',
      url,
    }).then(extractData)
  }

  const getAllFilters = (
    idList,
    pageSize = 20,
    pageNumber = 1,
    orderBy = 'Id',
    orderDescending = false,
    name = '',
    category,
    type,
  ) => {
    const loginType = storeGet(LOGIN_TYPE_KEY)
    const hidden =
      loginType === LoginTypeEnum.AdmLogin.ordinal ||
      loginType === LoginTypeEnum.AdmLoginLimited.ordinal
        ? undefined
        : false
    const url = endpoints.getAllFilters({
      idList,
    })

    const types = type ? (Array.isArray(type) ? type : [type]) : []

    const query = {
      pageSize,
      pageNumber,
      orderBy,
      orderDescending,
      search: name,
      categories: category ? [category] : [],
      hidden,
      types,
    }

    return apiWrapper({
      method: 'post',
      url,
      data: JSON.stringify(query),
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(extractData)
  }

  const createFilter = (filter) => {
    const url = endpoints.createFilter

    return apiWrapper({
      method: 'post',
      url,
      data: JSON.stringify(filter),
    }).then(extractData)
  }

  const getFilter = (idFilter) => {
    const url = endpoints.getFilter(idFilter)

    return apiWrapper({
      method: 'get',
      url,
    }).then(extractData)
  }

  const deleteFilter = (idFilter) => {
    const url = endpoints.deleteFilter(idFilter)

    return apiWrapper({
      method: 'delete',
      url,
    }).then(extractData)
  }

  const updateFilter = (filter) => {
    const url = endpoints.updateFilter(filter.Id)

    return apiWrapper({
      method: 'put',
      url,
      data: JSON.stringify(filter),
    }).then(extractData)
  }

  //  categories CRUD
  const getAllCategories = (
    pageSize,
    pageNumber,
    orderBy,
    orderDescending,
    name,
  ) => {
    const url = endpoints.getAllCategories({
      pageSize,
      pageNumber,
      orderBy,
      orderDescending,
      name,
    })

    return apiWrapper({
      method: 'get',
      url,
    }).then(extractData)
  }

  const createCategory = (category) => {
    const url = endpoints.createCategory

    return apiWrapper({
      method: 'post',
      url,
      data: JSON.stringify(category),
    }).then(extractData)
  }

  const getCategory = (idCategory) => {
    const url = endpoints.getCategory(idCategory)

    return apiWrapper({
      method: 'get',
      url,
    }).then(extractData)
  }

  const getCategoryUsage = (idCategory) => {
    const url = endpoints.getCategoryUsage(idCategory)

    return apiWrapper({
      method: 'get',
      url,
    }).then(extractData)
  }

  const deleteCategory = (idCategory) => {
    const url = endpoints.deleteCategory(idCategory)

    return apiWrapper({
      method: 'delete',
      url,
    }).then(extractData)
  }

  const updateCategory = (idCategory, category) => {
    const url = endpoints.updateCategory(idCategory)

    return apiWrapper({
      method: 'put',
      url,
      data: JSON.stringify(category),
    }).then(extractData)
  }

  //

  const beFiltersApi = {
    processFilterByConfiguration,
    processFilterById,
    //
    simulateFilterByConfiguration,
    simulateFilterById,
    //
    getAllFilters,
    createFilter,
    getFilter,
    deleteFilter,
    updateFilter,
    //
    getAllCategories,
    createCategory,
    getCategory,
    getCategoryUsage,
    deleteCategory,
    updateCategory,
  }

  return (
    <BeFiltersApiContext.Provider value={beFiltersApi}>
      {children}
    </BeFiltersApiContext.Provider>
  )
}

BeFiltersApiProviderComponent.defaultProps = {
  children: null,
}

BeFiltersApiProviderComponent.propTypes = {
  children: PropTypes.node,
  apiWrapper: PropTypes.func.isRequired,
}

export const BeFiltersApiProvider = withApi(BeFiltersApiProviderComponent)

export class ObjectTypeEnum extends Enum {}
ObjectTypeEnum.initEnum([
  'All',
  'Anagraphic',
  'Activity',
  'Device',
  'Geolocation',
  'MultiFilter',
  'MultiType',
  'Engagement',
])

export class ActionTypeEnum extends Enum {}
ActionTypeEnum.initEnum(['Nil', 'And', 'Or', 'AndNot'])

export class DatesTypeEnum extends Enum {}
DatesTypeEnum.initEnum(['DaysDetails', 'HoursDetails', 'DateRangeDetails'])

export class SpecTypeEnum extends Enum {}
SpecTypeEnum.initEnum([
  'GroupSpec',
  'TextSpec',
  'NumericSpec',
  'DateSpec',
  'DateTimeSpec',
  'GeoSpec',
  'DeviceSpec',
  'SysSpec',
  'EmailSpec',
  'SmsSpec',
  'SubscriptionSpec',
  'ProfileSpec',
  'FilterSpec',
  'EngagementSpec',
])

export class DatesDetailsTypeEnum extends Enum {}
DatesDetailsTypeEnum.initEnum(['Exactly', 'Last'])

export class SubscriptionMatchEnum extends Enum {}
SubscriptionMatchEnum.initEnum([
  'SmsSubscribed',
  'SmsUnsubscribed',
  'EmailSubscribed',
  'EmailUnsubscribed',
])

export class SmsMatchEnum extends Enum {}
SmsMatchEnum.initEnum([
  'SmsSent',
  'SmsNotSent',
  'SmsSuccessfullySent',
  'SmsNotReceived',
  'SmsExpired',
  'SmsRejected',
])

export class EmailMatchEnum extends Enum {}
EmailMatchEnum.initEnum([
  'EmailOpened',
  'EmailClicked',
  'EmailNotOpened',
  'EmailNotClicked',
  'EmailSent',
  'EmailSuccessfullySent',
  'EmailNotSent',
  'EmailBouncedHB',
  'EmailBouncedSB',
  'EmailNotBouncedHB',
  'EmailPerformedAtLeast1Action',
  'EmailPerformedLessThan5Actions',
  'EmailPerformedLessThan10Actions',
  'EmailPerformed10OrMoreActions',
])

export class GeoMatchEnum extends Enum {}
GeoMatchEnum.initEnum(['LocatedIn', 'NotLocatedIn'])

export class GroupMatchEnum extends Enum {}
GroupMatchEnum.initEnum(['BelongsTo', 'NotBelongsTo'])

export class DateFormatEnum extends Enum {}
DateFormatEnum.initEnum([
  'DateFormat1',
  'DateFormat2',
  'DateFormat3',
  'DateFormat4',
  'DateFormat5',
  'DateFormat6',
  'DateFormat7',
  'DateFormat8',
])

export class DateTimeFormatEnum extends Enum {}
DateTimeFormatEnum.initEnum(['DateFormat9'])

export class ChannelTypeEnum extends Enum {}
ChannelTypeEnum.initEnum(['All', 'Email', 'Sms'])

export class TextMatchEnum extends Enum {}
TextMatchEnum.initEnum([
  'TextPrefix',
  'TextNotPrefix',
  'TextSuffix',
  'TextNotSuffix',
  'TextContains',
  'TextNotContains',
  'TextIn',
  'TextNotIn',
  'TextNullEmpty',
  'TextNotNullNotEmpty',
  'TextSame',
  'TextGreater',
  'TextGreaterEqual',
  'TextLess',
  'TextLessEqual',
  'TextDifferent',
])

export class NumericFormatEnum extends Enum {}
NumericFormatEnum.initEnum(['Decimal', 'Integer'])

export class NumericMatchEnum extends Enum {}
NumericMatchEnum.initEnum([
  'NumericSame',
  'NumericGreater',
  'NumericGreaterEqual',
  'NumericLess',
  'NumericLessEqual',
  'NumericDifferent',
])

export class DateMatchEnum extends Enum {}
DateMatchEnum.initEnum([
  'PlusXdaysEqualsToToday',
  'MinusXdaysEqualsToToday',
  'EqualToToday',
  'PlusXDaysEarlierThanToday',
  'PlusXdaysLaterThanToday',
  'EqualTo',
  'Before',
  'After',
  'MinusXdaysEarlierThanToday',
  'MinusXdaysLaterThanToday',
])

export class DateTimeMatchEnum extends Enum {}
DateTimeMatchEnum.initEnum(['PlusXHoursEqualsToNow'])

export class EngagementMatchEnum extends Enum {}
EngagementMatchEnum.initEnum(['EqualTo'])

export class EngagementScoreEnum extends Enum {}
EngagementScoreEnum.initEnum([
  'Bounce',
  'New',
  'Inactive',
  'LessActive',
  'Active',
  'VeryActive',
  'Excluded',
])
